import { useEffect, useRef, useState } from 'react';
import type { OrderQueryMessage } from '../api/types';
import { Button, Note, Panel } from './index';

/**
 * OR-14 — an order's conversation, as a chat. Shared by A-15 (Miraki) and the
 * retailer's order detail; only which side is "mine" and the words differ, so
 * both are passed in — the admin console speaks plain English and the shop
 * speaks through i18n, and this component takes either.
 *
 * Mine on the right, theirs on the left, oldest first, with the newest in view.
 * Enter sends, Shift+Enter breaks a line. A failed send keeps what was typed.
 */
export function OrderChat(
  { messages, mySide, canSend, onSend, labels }: {
    messages: readonly OrderQueryMessage[];
    mySide: 'retailer' | 'miraki';
    canSend: boolean;
    onSend: (body: string) => Promise<unknown>;
    labels: {
      title: string; empty: string; placeholder: string; send: string; sending: string;
      closed: string; failed: string;
    };
  },
) {
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const list = useRef<HTMLDivElement>(null);

  // Newest in view whenever a message arrives — sent here or polled in.
  useEffect(() => {
    const el = list.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const send = async () => {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    setError(null);
    try {
      await onSend(body);
      setDraft('');
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : labels.failed);
    } finally {
      setSending(false);
    }
  };

  return (
    <Panel title={labels.title}>
      <div className="mk-chat" ref={list} aria-live="polite">
        {messages.length === 0 ? <p className="mk-dim mk-chat-empty">{labels.empty}</p> : null}
        {messages.map((m, i) => (
          <div key={`${m.sentAt}-${i}`} className={m.side === mySide ? 'mk-chat-msg mine' : 'mk-chat-msg'}>
            <div className="mk-chat-meta">
              <b>{m.author}</b> · {new Date(m.sentAt).toLocaleString()}
            </div>
            <div className="mk-chat-body">{m.body}</div>
          </div>
        ))}
      </div>
      {canSend ? (
        <div className="mk-chat-send">
          <textarea
            className="mk-fld"
            rows={2}
            value={draft}
            maxLength={4000}
            placeholder={labels.placeholder}
            aria-label={labels.placeholder}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); }
            }}
          />
          <Button disabled={sending || draft.trim() === ''} onClick={() => void send()}>
            {sending ? labels.sending : labels.send}
          </Button>
        </div>
      ) : (
        <div className="mk-chat-send"><Note>{labels.closed}</Note></div>
      )}
      {error ? <p className="mk-chat-error">{error}</p> : null}
    </Panel>
  );
}
